import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { useSession } from '@/lib/auth/session';
import { queryKeys } from '@/lib/queryKeys';

/**
 * 홈 인사말 (DOC-66).
 *
 * ## 무엇을 말할지는 서버가 정한다
 *
 * 비가 오는지, 한 시간 뒤에 회의가 있는지, 내일이 연차인지 — 그 판단에 필요한 자료는 전부 서버에
 * 있다. 화면이 위젯들의 데이터를 긁어모아 문장을 조립하면 위젯 하나가 늦을 때마다 인사말이 바뀌고,
 * 위젯을 끈 사람은 그 화제를 영영 못 듣는다.
 *
 * ## 새로고침해도 문장이 바뀌지 않는다
 *
 * 서버가 **시드**로 뽑기 때문이다 — 같은 사람·같은 날·같은 시간대·같은 90분 구간·같은 로그인이면
 * 같은 문장이 나온다. 화면이 할 일은 **언제 다시 물을지**뿐이고, 그것도 서버가 준 `expiresAt`
 * 까지를 `staleTime` 으로 잡는 것으로 끝난다. 폴링도 타이머도 없다.
 *
 * 여기서 화면이 서버에 알려 주는 것은 둘이다.
 *  - `loginAt` — 이 세션이 언제 시작됐나. 다시 로그인하면 값이 바뀌어 새 문장이 나온다
 *  - `recent` — 최근에 나온 화제. 같은 이야기가 연달아 나오지 않게 서버가 후보에서 뺀다
 */

export type HomeGreeting = {
  /** AI 인사말이 켜져 있나. 꺼져 있으면 `body` 가 null 이고 `lead` 는 예전 그대로다. */
  enabled: boolean;
  lead: string;
  body: string | null;
  /** 무엇을 근거로 말했나. 화면은 최근 화제를 기억하는 데만 쓴다. */
  signal: string;
  bucket: string | null;
  action: { label: string; href: string } | null;
  /** 이 문장이 유효한 끝 시각(ISO). 그때까지 다시 묻지 않는다. */
  expiresAt: string;
  /** 모델이 다듬은 틀인가, 코드 상수 틀인가. 진단용이다. */
  polished: boolean;
};

const GREETING_PATH = '/v1/home/greeting';
const PREFERENCE_PATH = '/v1/home/greeting/preference';

/** 이 탭의 로그인 시각. 탭을 닫으면 사라지므로 다시 들어오면 새 문장이 나온다. */
const LOGIN_AT_KEY = 'bomi.home.greeting.loginAt';

/** 최근에 나온 화제. 같은 이야기가 연달아 나오지 않게 서버로 되돌려 보낸다. */
const RECENT_KEY = 'bomi.home.greeting.recent';
const RECENT_LIMIT = 3;

/** 서버 응답이 없을 때까지 화면을 비워 두지 않는다 — 지금과 똑같은 인사말로 시작한다. */
export function fallbackGreeting(name: string | undefined): HomeGreeting {
  return {
    enabled: false,
    lead: name ? `${name} 님, 안녕하세요` : '홈',
    body: null,
    signal: 'OFF',
    bucket: null,
    action: null,
    expiresAt: '',
    polished: false,
  };
}

export function useHomeGreeting() {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;

  return useQuery({
    queryKey: [...queryKeys.homeGreeting, userId],
    queryFn: async () => {
      const params = new URLSearchParams({ loginAt: loginAt() });
      for (const signal of readRecent()) params.append('recent', signal);

      const greeting = await apiFetch<HomeGreeting>(`${GREETING_PATH}?${params}`);
      rememberRecent(greeting.signal);
      return greeting;
    },
    enabled: !!userId,
    // 서버가 정한 끝 시각까지는 같은 답이 나온다 — 다시 물을 이유가 없다.
    staleTime: (query) => msUntilExpiry(query.state.data?.expiresAt),
    // 인사말이 안 떠도 홈은 떠야 한다. 실패를 물고 늘어지지 않는다.
    retry: false,
  });
}

// ── 켬/끔 ──────────────────────────────────────────────────────────────────────
//
// **기기가 아니라 계정에 남긴다.** 쉬운 모드(`useEasyMode`)는 기기에 남는데, 그건 같은 사람이라도
// 기기마다 답이 다른 설정이기 때문이다(현장 휴대폰에서는 켜고 사무실 모니터에서는 끈다).
// 인사말은 그 반대로 **사람의 취향**이다 — AI 가 말 거는 게 싫은 사람은 어디서든 싫다.

export function useGreetingPreference() {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;
  const queryClient = useQueryClient();
  const key = [...queryKeys.homeGreetingPreference, userId] as const;

  const query = useQuery({
    queryKey: key,
    // 못 읽으면 켜진 것으로 본다. **기본값이 켜짐**이므로(사용자 지시 2026-08-27 — 먼저 꺼 두고
    // 써 본 뒤 뒤집었다) 모르는 상태와 기본 상태가 같은 값이고, 스위치가 잠깐 깜빡이지 않는다.
    queryFn: () => apiFetch<{ enabled: boolean }>(PREFERENCE_PATH).catch(() => ({ enabled: true })),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });

  const save = useMutation({
    mutationFn: (enabled: boolean) =>
      apiFetch<{ enabled: boolean }>(PREFERENCE_PATH, { method: 'PUT', body: { enabled } }),
    onSuccess: (result) => {
      queryClient.setQueryData(key, result);
      // 켜고 끈 것은 곧바로 h1 에 보여야 한다. 인사말은 캐시가 길어(expiresAt) 스스로 다시 오지 않는다.
      void queryClient.invalidateQueries({ queryKey: queryKeys.homeGreeting });
      /*
        **떠 있는 오브도 같이 조용해진다**(사용자 지시 2026-09-18). 이 묶음은 십 분 캐시라, 여기서
        버리지 않으면 방금 끈 사람에게 오브가 십 분을 더 떠든다 — 스위치를 누르고도 달라지는 게
        없으면 사용자는 그 스위치를 믿지 않게 된다.
      */
      void queryClient.invalidateQueries({ queryKey: queryKeys.orbLines });
    },
  });

  return { enabled: query.data?.enabled ?? true, save };
}

// ── 기기에 적어 두는 것 ────────────────────────────────────────────────────────

function loginAt(): string {
  try {
    const saved = window.sessionStorage.getItem(LOGIN_AT_KEY);
    if (saved) return saved;

    const now = new Date().toISOString();
    window.sessionStorage.setItem(LOGIN_AT_KEY, now);
    return now;
  } catch {
    // 사생활 보호 모드에서 접근이 막혔을 뿐이다. 소금이 없으면 서버가 그만큼만 덜 흔든다.
    return new Date().toISOString();
  }
}

function readRecent(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

function rememberRecent(signal: string): void {
  if (!signal || signal === 'OFF') return;

  try {
    const next = [signal, ...readRecent().filter((item) => item !== signal)].slice(0, RECENT_LIMIT);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // 기억하지 못하면 같은 화제가 한 번 더 나올 뿐이다. 되돌릴 것이 없다.
  }
}

/** `expiresAt` 까지 남은 밀리초. 이미 지났거나 값이 없으면 0 — 다음 조회에서 새로 받는다. */
function msUntilExpiry(expiresAt: string | undefined): number {
  if (!expiresAt) return 0;

  const remaining = new Date(expiresAt).getTime() - Date.now();
  return Number.isFinite(remaining) && remaining > 0 ? remaining : 0;
}
